import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { hubApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Broadcast, Lightning, BookOpen, ArrowRight,
  CircleNotch, CheckCircle, Warning, SignOut,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

function StatRow({ label, value, accent }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[12px] text-slate-500">{label}</span>
      <span className={`font-mono text-[13px] font-semibold tabular-nums ${accent || "text-slate-700"}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function StatusDot({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${ok ? "text-emerald-600" : "text-slate-400"}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-300"}`} />
      {label}
    </span>
  );
}

function ModuleCard({ icon: Icon, accentClass, title, subtitle, stats, cta, path, badge }) {
  const navigate = useNavigate();
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col hover:shadow-md hover:border-slate-300 transition-all duration-150 cursor-pointer group"
      onClick={() => navigate(path)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg ${accentClass} flex items-center justify-center shrink-0`}>
          <Icon size={20} weight="fill" className="text-white" />
        </div>
        {badge && (
          <span className="text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>

      {/* Title */}
      <h2 className="font-display text-[17px] font-bold text-slate-900 leading-tight mb-0.5">
        {title}
      </h2>
      <p className="text-[12px] text-slate-400 mb-5">{subtitle}</p>

      {/* Stats */}
      <div className="space-y-2 flex-1">
        {stats.map((s) => (
          <StatRow key={s.label} label={s.label} value={s.value} accent={s.accent} />
        ))}
      </div>

      {/* CTA */}
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
        <span className="text-[13px] font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">
          {cta}
        </span>
        <ArrowRight
          size={15}
          className="text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all"
        />
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)  return "Just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function HubPage() {
  const { user, logout } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hubApi.summary()
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load platform summary"))
      .finally(() => setLoading(false));
  }, []);

  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase()).join("");

  const ins = data?.insights  || {};
  const hrm = data?.hermes    || {};
  const kb  = data?.knowledge || {};

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 px-8 h-14 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-slate-950 flex items-center justify-center">
            <Lightning weight="fill" size={14} className="text-teal-400" />
          </div>
          <span className="font-display text-[15px] font-bold text-slate-900 tracking-tight">
            Hermes Intelligence
          </span>
          <span className="text-[11px] text-slate-400 font-mono uppercase tracking-widest ml-1">
            Platform
          </span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 transition-colors">
              <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">
                {initials || "U"}
              </div>
              <span className="text-[13px] text-slate-600 font-medium hidden sm:block truncate max-w-[140px]">
                {user?.name || user?.email}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>
              <div className="text-[10px] text-slate-400 font-normal uppercase tracking-wider">Signed in as</div>
              <div className="text-xs font-medium text-slate-900 truncate mt-0.5">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="cursor-pointer text-xs">
              <SignOut size={13} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main */}
      <main className="flex-1 px-8 py-10 max-w-5xl mx-auto w-full">
        {/* Heading */}
        <div className="mb-8">
          <h1 className="font-display text-[28px] font-bold text-slate-900 tracking-tight leading-tight">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}.
          </h1>
          <p className="text-[14px] text-slate-500 mt-1">
            {loading
              ? "Loading platform status…"
              : hrm.last_active_at
              ? `Hermes last active ${timeAgo(hrm.last_active_at)}. ${hrm.pending_tasks > 0 ? `${hrm.pending_tasks} task${hrm.pending_tasks > 1 ? "s" : ""} pending.` : "No pending tasks."}`
              : "Hermes hasn't run yet. Open the Control Center to configure and start a cycle."}
          </p>
        </div>

        {/* Module cards */}
        {loading ? (
          <div className="grid gap-5 grid-cols-1 md:grid-cols-3">
            {[1,2,3].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl h-64 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid gap-5 grid-cols-1 md:grid-cols-3">
            <ModuleCard
              icon={Broadcast}
              accentClass="bg-teal-500"
              title="Insights Evolution"
              subtitle="Intelligence feed, vault, decisions"
              path="/insights"
              badge={hrm.suggestions_pending > 0 ? `${hrm.suggestions_pending} suggestions` : null}
              stats={[
                { label: "Total signals",    value: ins.total,                                               accent: "text-slate-900" },
                { label: "Avg confidence",   value: ins.avg_confidence ? `${Math.round(ins.avg_confidence)}%` : "—", accent: ins.avg_confidence >= 70 ? "text-emerald-600" : "text-amber-600" },
                { label: "Learnings",        value: ins.learnings,                                           accent: "text-emerald-600" },
                { label: "Vault items",      value: ins.vault },
              ]}
              cta="Open Intelligence Feed"
            />

            <ModuleCard
              icon={Lightning}
              accentClass="bg-indigo-600"
              title="Hermes Control"
              subtitle="Identity, behavior, chat"
              path="/hermes/chat"
              badge={hrm.pending_tasks > 0 ? `${hrm.pending_tasks} pending` : null}
              stats={[
                { label: "Last active",      value: timeAgo(hrm.last_active_at) },
                { label: "Pending tasks",    value: hrm.pending_tasks,           accent: hrm.pending_tasks > 0 ? "text-amber-600" : "text-slate-700" },
                { label: "Chat messages",    value: hrm.chat_messages },
                { label: "Active skills",    value: hrm.skills },
              ]}
              cta="Open Control Center"
            />

            <ModuleCard
              icon={BookOpen}
              accentClass="bg-violet-600"
              title="Knowledge Base"
              subtitle="SilverBullet KB, semantic search"
              path="/knowledge"
              stats={[
                { label: "KB chunks",        value: kb.chunks },
                { label: "Qdrant status",    value: <StatusDot ok={kb.qdrant_healthy} label={kb.qdrant_healthy ? "Healthy" : "Offline"} /> },
                { label: "Active skills",    value: hrm.skills },
                { label: "",                 value: "" },
              ]}
              cta="Open Knowledge Base"
            />
          </div>
        )}

        {/* Quick links */}
        <div className="mt-8 flex items-center gap-6 text-[12px] text-slate-400">
          <span>Quick access:</span>
          {[
            { label: "Trend Radar",   path: "/insights" },
            { label: "Vault",         path: "/insights/vault" },
            { label: "Hermes Chat",   path: "/hermes/chat" },
            { label: "Identity",      path: "/hermes/identity" },
            { label: "Operator",      path: "/hermes/operator" },
          ].map((l) => (
            <button
              key={l.path}
              onClick={() => window.location.href = l.path}
              className="text-slate-500 hover:text-slate-800 transition-colors font-medium"
            >
              {l.label}
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
